import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, Check, ChevronDown, ChevronRight } from "lucide-react";
import { journeyFor, optsForDeal, bsaStatus } from "../../data/transactionJourney";
import { api } from "../../lib/api";

/* Key Dates — read-only summary of the existing canonical dates.
 * Event dates = milestone `completed_at`; planned = pending `target_date`.
 * MoS issued = MoS status `sent_at` (latest memorandum_of_sale record; chosen date for external sends);
 * enquiry last response = newest `answered_at` on the deal's enquiries, else "Completed" once replies are ticked.
 * Exchange / completion targets live in the header; rows appear only once confirmed. */

const DAY_MS = 86400000;
const RISK_STATES = new Set(["blocked", "at_risk", "at-risk"]);
const SEARCH_RECEIPTS = ["local_authority_search_received", "water_drainage_search_received", "environmental_search_received"];

const isoDay = (v) => (v ? String(v).slice(0, 10) : null);
const isDone = (m) => !!m && (m.state === "done" || m.state === "complete" || !!m.completed_at);

export function formatKeyDate(iso) {
  const d = iso ? new Date(`${isoDay(iso)}T00:00:00`) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("en-GB", sameYear ? { day: "numeric", month: "short" } : { day: "numeric", month: "short", year: "numeric" });
}

// Signed UK calendar-day difference (target - today, Europe/London).
const ukDayDiff = (iso) => {
  const day = isoDay(iso);
  if (!day) return null;
  const target = Date.parse(`${day}T00:00:00Z`);
  const today = Date.parse(`${new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" })}T00:00:00Z`);
  return Number.isNaN(target) ? null : Math.round((target - today) / DAY_MS);
};

export function daysSince(iso) {
  const diff = ukDayDiff(iso);
  return diff == null ? null : Math.max(0, -diff);
}

// One date cell from a milestone: recorded event, planned target, or missing.
const cellFor = (label, m, doneTone = "recorded") => {
  if (isDone(m)) return { label, date: isoDay(m.completed_at), tone: doneTone };
  if (m?.target_date) return { label, date: isoDay(m.target_date), tone: "planned" };
  return { label, date: null, tone: "missing" };
};

// Elapsed time since an ordered / submitted event until the response lands.
const waitingFor = (fromM, received, riskM) => {
  if (!isDone(fromM) || received || !fromM.completed_at) return null;
  const days = daysSince(fromM.completed_at);
  if (days == null) return null;
  const risky = (Array.isArray(riskM) ? riskM : [riskM]).some((m) => RISK_STATES.has(String(m?.state || "").toLowerCase()));
  return { days, flagged: risky };
};

const surveyRow = (by) => {
  if (!by.survey_instructed) return null;
  const instructed = cellFor("Survey instructed", by.survey_instructed);
  return { id: "survey", label: "Survey", stageId: "survey", hint: instructed.tone === "missing" ? "Optional" : null, cells: [instructed] };
};

// Single canonical record: MoS status. Actual sent date only — never the recording timestamp.
const mosRow = (mos) => {
  const cell = !mos?.completed
    ? { label: "Issued", date: null, tone: "missing" }
    : mos.sent_at
      ? { label: "Issued", date: isoDay(mos.sent_at), tone: "recorded" }
      : { label: "Issued", date: null, tone: "none", text: "Not recorded" };
  return { id: "mos", label: "Memorandum of Sale", stageId: "memo", cells: [cell] };
};

// Issued = seller's solicitor issues the draft contract pack; Received = buyer's solicitor acknowledges it.
const contractRow = (by) => {
  const issued = by.draft_contract_issued;
  const received = by.buyer_solicitor_acknowledged_contract_pack;
  if (!issued && !received) return null;
  return {
    id: "contract", label: "Contract", stageId: "contract",
    detail: "Seller's solicitor → buyer's solicitor",
    cells: [cellFor("Issued", issued), cellFor("Received", received, "confirmed")],
  };
};

const enquiriesRow = (by, enquiries) => {
  const raised = by.enquiries_raised;
  if (!raised) return null;
  const answered = (enquiries || []).map((e) => isoDay(e?.answered_at)).filter(Boolean).sort();
  const last = answered[answered.length - 1];
  const repliesDone = isDone(by.enquiries_replies_received) || isDone(by.enquiries_resolved);
  const response = last ? { label: "Last response", date: last, tone: "recorded" }
    : { label: "Last response", date: null, tone: "none", text: repliesDone ? "Completed" : "None yet" };
  return {
    id: "enquiries", label: "Enquiries", stageId: "enquiries",
    cells: [cellFor("Raised", raised), response],
  };
};

// Exchange / completion: header owns the targets; the row only shows a confirmed actual date.
const confirmedEventRow = (id, label, stageId, cellLabel, m, fallbackIso) => {
  const iso = isDone(m) ? (m.completed_at || fallbackIso) : fallbackIso;
  if (!iso) return null;
  return { id, label, stageId, cells: [{ label: cellLabel, date: isoDay(iso), tone: "confirmed" }] };
};

const searchesRow = (by) => {
  const ordered = by.searches_ordered;
  const receipts = SEARCH_RECEIPTS.map((id) => by[id]).filter(Boolean);
  if (!ordered && receipts.length === 0) return null;
  const doneReceipts = receipts.filter(isDone);
  const allIn = (receipts.length > 0 && doneReceipts.length === receipts.length) || isDone(by.searches_returned);
  let results;
  if (allIn) {
    const dates = (doneReceipts.length ? doneReceipts : [by.searches_returned]).map((m) => isoDay(m.completed_at)).filter(Boolean).sort();
    results = { label: "Results received", date: dates[dates.length - 1] || null, tone: "confirmed" };
  } else if (doneReceipts.length > 0) {
    results = { label: "Results received", date: null, tone: "partial", text: `${doneReceipts.length} of ${receipts.length}` };
  } else {
    const planned = receipts.map((m) => isoDay(m.target_date)).filter(Boolean).sort()[0];
    results = { label: "Results received", date: planned || null, tone: planned ? "planned" : "missing" };
  }
  return {
    id: "searches", label: "Searches", stageId: "contract",
    cells: [cellFor("Ordered", ordered), results],
    waiting: waitingFor(ordered, allIn, receipts),
  };
};

const bsaRow = (by, deal, docRequests) => {
  const req = by.bsa_leaseholder_deed_submitted_to_landlord;
  const recv = by.bsa_landlords_certificate_received;
  if (!req && !recv) return null;
  const flag = deal?.applicable_bsa ?? deal?.applicableBSA ?? null;
  const status = flag === true ? "yes" : flag === false ? "no" : bsaStatus(deal?.tenure, optsForDeal(deal || {}));
  const anyRecorded = [req, recv].some((m) => isDone(m) || m?.target_date);
  if (status === "no" || (status !== "yes" && !anyRecorded)) return null;
  const docReq = (docRequests || []).find((r) => /bsa|building safety/i.test(`${r?.doc_type || ""} ${r?.label || ""}`));
  const docType = docReq?.label || req?.note || null;
  const recipient = docReq?.recipient_name || docReq?.requested_from_label || null;
  return {
    id: "bsa", label: "BSA", stageId: "bsa",
    detail: [docType, recipient && `to ${recipient}`].filter(Boolean).join(" · ") || null,
    cells: [cellFor("Submitted", req), cellFor("Response received", recv, "confirmed")],
    waiting: waitingFor(req, isDone(recv), recv),
  };
};

export function buildKeyDates({ deal, milestones, docRequests, mos = null, enquiries = null }) {
  const items = Array.isArray(milestones?.items) ? milestones.items : [];
  const by = Object.fromEntries(items.filter((m) => m?.id).map((m) => [m.id, m]));
  const tenure = String(deal?.tenure || deal?.property?.tenure || "freehold").toLowerCase();
  const stepIds = new Set(journeyFor(tenure, optsForDeal(deal || {})).map((n) => n.id));
  const rows = [mosRow(mos)];
  // Mortgage stays conditional on the Journey (hidden for cash deals).
  if (stepIds.has("mortgage") && (by.mortgage_application_submitted || by.mortgage_offer)) {
    rows.push({
      id: "mortgage", label: "Mortgage", stageId: "mortgage",
      detail: by.mortgage_offer?.secondary_date ? `Offer expires ${formatKeyDate(by.mortgage_offer.secondary_date)}` : null,
      cells: [
        cellFor("Application submitted", by.mortgage_application_submitted),
        cellFor("Valuation booked", by.valuation_booked),
        cellFor("Offer received", by.mortgage_offer, "confirmed"),
      ],
      waiting: waitingFor(by.mortgage_application_submitted, isDone(by.mortgage_offer), by.mortgage_offer),
    });
  }
  rows.push(surveyRow(by));
  rows.push(searchesRow(by));
  if (stepIds.has("mgmt_pack") && (by.management_pack_requested || by.management_pack_received)) {
    rows.push({
      id: "mgmt_pack", label: "Management Pack", stageId: "mgmt_pack",
      cells: [cellFor("Ordered", by.management_pack_requested), cellFor("Received", by.management_pack_received, "confirmed")],
      waiting: waitingFor(by.management_pack_requested, isDone(by.management_pack_received), by.management_pack_received),
    });
  }
  if (stepIds.has("bsa")) rows.push(bsaRow(by, deal, docRequests));
  rows.push(contractRow(by));
  rows.push(enquiriesRow(by, enquiries));
  // A ticked terminal row shows only once the lifecycle is confirmed (same gate as the Journey).
  const life = [String(deal?.stage || "").toLowerCase(), String(deal?.status || "").toLowerCase()];
  const exchanged = life.some((v) => v === "exchanged" || v === "completed");
  const completed = life.includes("completed");
  rows.push(confirmedEventRow("exchange", "Exchange", "exchange", "Exchanged", exchanged ? by.contracts_exchanged : null, deal?.exchanged_at));
  rows.push(confirmedEventRow("completion", "Completion", "complete", "Completed", completed ? by.completion_date : null, deal?.completed_at));
  return rows.filter(Boolean);
}

// "X recorded" = populated actual dates only (planned, partial, empty and waiting excluded).
export const countRecorded = (rows) =>
  rows.reduce((n, r) => n + r.cells.filter((c) => (c.tone === "recorded" || c.tone === "confirmed") && c.date).length, 0);

const TONE_CLASS = {
  planned: "text-slate-500 italic",
  target: "text-slate-600 italic font-semibold text-[12px]",
  recorded: "text-slate-900 font-semibold",
  confirmed: "text-slate-900 font-semibold",
  partial: "text-slate-700 font-semibold",
  none: "text-slate-400",
};

const DateCell = ({ cell, testid }) => {
  const when = formatKeyDate(cell.date);
  const saved = cell.tone === "recorded" || cell.tone === "confirmed";
  return (
    <span className="inline-flex items-center gap-1 text-[11.5px] leading-tight whitespace-nowrap" data-testid={testid} data-tone={cell.tone}>
      {cell.label && <span className="text-slate-500">{cell.label}</span>}
      {cell.tone === "missing" ? (
        <span className="font-semibold text-violet-600" data-testid={`${testid}-add`}>
          + Add<span className="sr-only"> date, not recorded</span>
        </span>
      ) : (
        <>
          {saved && <Check size={11} strokeWidth={2.6} className="text-emerald-500" aria-hidden />}
          <span className={`tabular-nums ${TONE_CLASS[cell.tone]}`}>
            {cell.tone === "partial" || cell.tone === "none" ? cell.text : when || "Recorded"}
            {(cell.tone === "planned" || cell.tone === "target") && <span className="sr-only"> (planned)</span>}
          </span>
        </>
      )}
    </span>
  );
};

const KeyDateRow = ({ row, onOpen }) => (
  <li>
    <button
      type="button"
      onClick={() => onOpen?.(row.stageId)}
      className="w-full min-h-[44px] flex items-start sm:items-center gap-3 px-2 py-2.5 rounded-lg text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300 transition-colors"
      aria-label={`${row.label} dates · open ${row.label} milestone details`}
      data-testid={`key-dates-row-${row.id}`}
    >
      <span className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
        <span className="sm:w-[132px] shrink-0">
          <span className="block text-[12.5px] font-semibold text-slate-900 leading-tight">{row.label}</span>
          {(row.hint || row.detail) && (
            <span className="block text-[10.5px] text-slate-400 leading-tight mt-0.5 truncate" data-testid={`key-dates-row-${row.id}-detail`}>
              {row.hint || row.detail}
            </span>
          )}
        </span>
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
          {row.cells.map((c, i) => <DateCell key={c.label} cell={c} testid={`key-dates-${row.id}-cell-${i}`} />)}
          {row.countdown && (
            <span
              className={`inline-flex items-center h-5 px-1.5 rounded-md text-[10.5px] font-semibold tabular-nums ${row.countdown.overdue ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}
              data-testid={`key-dates-${row.id}-countdown`}
            >
              {row.countdown.text}
            </span>
          )}
          {row.waiting && (
            <span
              className={`inline-flex items-center h-5 px-1.5 rounded-md text-[10.5px] font-semibold tabular-nums ${row.waiting.flagged ? "bg-amber-50 text-amber-800" : "bg-slate-100 text-slate-600"}`}
              data-testid={`key-dates-${row.id}-waiting`}
            >
              Waiting {row.waiting.days} {row.waiting.days === 1 ? "day" : "days"}
            </span>
          )}
        </span>
      </span>
      <ChevronRight size={14} className="shrink-0 mt-0.5 sm:mt-0 text-slate-300" aria-hidden />
    </button>
  </li>
);

// Read-only fetch of the two non-milestone sources; re-runs whenever milestones reload.
const useKeyDateSources = (dealId, milestones) => {
  const [mos, setMos] = useState(null);
  const [enquiries, setEnquiries] = useState(null);
  useEffect(() => {
    if (!dealId) return undefined;
    let alive = true;
    api.get(`/api/v2/deals/${dealId}/memorandum-of-sale/status`).then(({ data }) => alive && setMos(data)).catch(() => {});
    api.get(`/api/v2/deals/${dealId}/enquiries`).then(({ data }) => alive && setEnquiries(Array.isArray(data) ? data : [])).catch(() => {});
    return () => { alive = false; };
  }, [dealId, milestones]);
  return { mos, enquiries };
};

export function KeyDatesSummary({ deal, milestones, docRequests, onOpenStage, bare = false }) {
  const [open, setOpen] = useState(true);
  const { mos, enquiries } = useKeyDateSources(deal?.id, milestones);
  const rows = useMemo(() => buildKeyDates({ deal, milestones, docRequests, mos, enquiries }), [deal, milestones, docRequests, mos, enquiries]);
  const recorded = countRecorded(rows);
  const loading = !milestones;
  // Phone: rendered inside the existing "Key dates" collapsible section — list only.
  if (bare) {
    if (loading) return <p className="text-[11.5px] text-slate-500 py-3" data-testid="key-dates-loading">Loading dates…</p>;
    return (
      <>
        <p className="flex items-center gap-2.5 pt-2 px-2 text-[10.5px] text-slate-500" data-testid="key-dates-legend">
          <span>{recorded} recorded</span>
          <span className="inline-flex items-center gap-1"><Check size={10} strokeWidth={2.6} className="text-emerald-500" aria-hidden />Recorded</span>
          <span className="italic">Planned</span>
        </p>
        <ul className="py-1 divide-y divide-slate-100" data-testid="key-dates-list">
          {rows.map((r) => <KeyDateRow key={r.id} row={r} onOpen={onOpenStage} />)}
        </ul>
      </>
    );
  }
  return (
    <section className="rounded-2xl bg-white ring-1 ring-slate-200/70" data-testid="key-dates-summary">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="key-dates-list"
        className="w-full min-h-[44px] flex items-center gap-3 px-5 py-3 text-left rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300"
        data-testid="key-dates-toggle"
      >
        <span className="h-7 w-7 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center shrink-0">
          <CalendarDays size={14} strokeWidth={2.2} />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[13.5px] font-semibold text-slate-900">Key Dates</span>
          <span className="block text-[11px] text-slate-500 mt-0.5" data-testid="key-dates-count">
            {loading ? "Loading dates…" : (
              <>
                {recorded} recorded
                <span className="hidden sm:inline-flex items-center gap-2.5 ml-2.5 text-slate-500" data-testid="key-dates-legend">
                  <span className="inline-flex items-center gap-1"><Check size={10} strokeWidth={2.6} className="text-emerald-500" aria-hidden />Recorded</span>
                  <span className="italic">Planned</span>
                </span>
              </>
            )}
          </span>
        </span>
        {open ? <ChevronDown size={15} className="text-slate-400" /> : <ChevronRight size={15} className="text-slate-400" />}
      </button>
      {open && !loading && (
        <ul id="key-dates-list" className="px-3 pb-3 divide-y divide-slate-100" data-testid="key-dates-list">
          {rows.map((r) => <KeyDateRow key={r.id} row={r} onOpen={onOpenStage} />)}
        </ul>
      )}
    </section>
  );
}

export default KeyDatesSummary;
